import React, { useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button, Card, CardContent, FormHelperText, Grid, MenuItem, Select, Stack, TextField, Typography } from "@mui/material";
import { useFormik, Form, FormikProvider } from "formik";
import * as Yup from "yup";
import { NOSPACE_REGEX } from "../../../utils/constants";
import errorHandler from "../../../utils/errorHandler";
import EdiorJodit from "../../../components/editor/editorJodit";
import { toast } from "react-toastify";
import { apiCms } from "../../../services/AllServices";

const editorContentValidation = (cmsData) => {

  if (cmsData === undefined) {
    return false;
  }

  const plainText = cmsData.replace(/<[^>]+>/g, "").trim();

  if (!plainText) {
    return false;
  }

  return true;
};

const validationSchema = Yup.object().shape({
  title: Yup.string()
    .required("Question is required")
    .matches(NOSPACE_REGEX, "Please Enter a valid question")
    .min(2, "Please enter at least 2 characters")
    .max(100, "Question must not be more than 100 characters."),
  status: Yup.mixed().required("Status is required"),
  description: Yup.string().test(
    "required-description",
    "Answer is required",
    (value) => editorContentValidation(value)
  ),
});

export default function AddEditFaq() {
  const navigate = useNavigate();
  const { id } = useParams();

  const formik = useFormik({
    initialValues: { title: "", description: "", status: "" },
    validationSchema,
    onSubmit: (values, { setSubmitting }) => {
      const apiCall = id
        ? apiCms.editFaq({
          id: id,
          title: values.title,
          description: values.description,
          status: values.status,
        })
        : apiCms.addFaq({
          title: values.title,
          description: values.description,
          status: values.status,
        });
      apiCall
        .then((res) => {
          toast.success(res?.data?.message);
          navigate("/faq");
          setSubmitting(false);
        })
        .catch((err) => {
          errorHandler(err);
          setSubmitting(false);
        });
    },
  });

  const { errors, touched, isSubmitting, handleSubmit, getFieldProps, setFieldValue } = formik;

  useEffect(() => {
    if (id) {
      apiCms
        .getByIdFaq({ id: id })
        .then((res) => {
          const data = res?.data?.data;
          formik.setValues({
            title: data.title,
            status: data.status,
            description: data.description, // Set the description directly in Formik values
          });
        })
        .catch((err) => {
          errorHandler(err);
        });
    }
  }, [id, formik]);
  return (
    <>
      <Card>
        <CardContent>
          <FormikProvider value={formik} style={{ padding: "34px 30px" }}>
            <Typography variant="h4" sx={{ mb: 2 }} align="start">
              {id ? "Edit" : "Add"} FAQ
            </Typography>
            <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
              <Stack spacing={3}>
                <TextField
                  name="title"
                  label="Question"
                  {...getFieldProps("title")}
                  error={Boolean(touched.title && errors.title)}
                  helperText={touched.title && errors.title}
                />
                
                <EdiorJodit
                  onChange={(html) => {
                    setFieldValue("description", html); 
                  }}
                  onBlur={() => formik.setFieldTouched("description", true)}  
                  data={formik.values.description}
                  placeholder="Answer"
                />

                {formik.touched.description && formik.errors.description && (
                  <FormHelperText error sx={{ display: 'block' }}>
                    {formik.errors.description}
                  </FormHelperText>
                )}

                <Grid item xs={12}>
                  <Select
                    labelId="status-label"
                    id="status"
                    value={formik.values.status}
                    onChange={(e) => formik.setFieldValue("status", e.target.value)}
                    fullWidth
                    error={Boolean(formik.touched.status && formik.errors.status)}
                    displayEmpty
                    defaultValue=""
                  >
                    <MenuItem value="">Select Status</MenuItem>
                    <MenuItem value={1}>Active</MenuItem>
                    <MenuItem value={0}>Inactive</MenuItem>
                  </Select>
                  {formik.touched.status && formik.errors.status && (
                    <FormHelperText error>{formik.errors.status}</FormHelperText>
                  )}
                </Grid>
              </Stack>
              <Stack
                spacing={2}
                mt={3}
                direction="row"
                alignItems="center"
                justifyContent="flex-end"
              >
                <Button
                  size="large"
                  type="button"
                  variant="contained"
                  disabled={isSubmitting}
                  sx={{ my: 2 }}
                  onClick={handleSubmit}
                >
                  {id ? "Update" : "Add"}
                </Button>

                <Button
                  size="large"
                  variant="contained"
                  onClick={() => {
                    if (!isSubmitting) navigate("/faq");
                  }}
                  sx={{ my: 2, ml: 4 }}
                >
                  Cancel
                </Button>
              </Stack>
            </Form>
          </FormikProvider>
        </CardContent>
      </Card>
    </>
  );
}
